// Module 3 / Slice 8 — organization unit tree screen.
//
// Read-first list view of organization_unit rows, optionally scoped to a
// single unit_type via the ?type=... query string. Mirrors the
// CodeSetIndexScreen idiom (TanStack Query + drawer-style edit deferred
// to Phase 4 follow-up C-mod3-1 / C-mod3-9).
//
// Per MODULE_3_PLAN.md v2 §10 the SPA stays behind the
// `organization.admin_ui.enabled` feature flag — wiring happens in the
// route guard, not the screen body.

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { organizationApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

export function UnitTreeScreen(): JSX.Element {
  const { t } = useI18n();
  const [typeFilter, setTypeFilter] = useState<string>("");

  const query = useQuery({
    queryKey: ["organization", "units", typeFilter],
    queryFn: async () => {
      const response = await organizationApiClient.GET("/organization/units", {
        params: {
          query: {
            limit: 200,
            archived: false,
            ...(typeFilter ? { type: typeFilter } : {}),
          },
        },
      });
      if (!response.data) {
        throw new Error("organization_units_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("organization.units.heading")}</h1>
      <p className="rc-help">{t("organization.units.description")}</p>

      <div style={{ marginBottom: "1rem" }}>
        <label htmlFor="org-unit-type-filter">
          {t("organization.units.filter.type")}
        </label>{" "}
        <input
          id="org-unit-type-filter"
          data-testid="unit-type-filter"
          type="text"
          value={typeFilter}
          placeholder={t("organization.units.filter.placeholder")}
          onChange={(e) => setTypeFilter(e.target.value.trim().toLowerCase())}
          style={{ marginLeft: "0.5rem" }}
        />
      </div>

      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length === 0 && (
        <p>{t("organization.units.empty")}</p>
      )}
      {query.data && query.data.items && query.data.items.length > 0 && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("organization.units.column.type")}</th>
              <th>{t("organization.units.column.name")}</th>
              <th>{t("organization.units.column.shortCode")}</th>
              <th>{t("organization.units.column.parent")}</th>
              <th>{t("organization.units.column.archived")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((u) => (
              <tr key={u.id} data-testid={`unit-row-${u.id}`}>
                <td><code>{u.unitTypeKey}</code></td>
                <td>{u.name}</td>
                <td>{u.shortCode ?? "\u2014"}</td>
                <td>
                  {u.parentOrganizationUnitId ? (
                    <code>{String(u.parentOrganizationUnitId).slice(0, 8)}…</code>
                  ) : (
                    <span aria-label="root">•</span>
                  )}
                </td>
                <td>{u.archived ? "\u2714" : "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
